import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProducts } from '../hooks/useProducts';
import ProductTable from './ProductTable';
import SearchBar from './SearchBar';
import AdminHeader from './AdminHeader';
import RetiredProductsManagement from './RetiredProductsManagement';
import ClientManagement from './ClientManagement';
import QuotationsManagement from './QuotationsManagement';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { token, isAdmin, isAuthenticated } = useAuth();
  const [activeFeature, setActiveFeature] = useState(null);

  // Don't render anything if not authenticated
  if (!token || !isAuthenticated() || !isAdmin()) {
    return null;
  }

  const handleFeatureClick = (feature) => {
    setActiveFeature(feature);
  };

  const handleBackToDashboard = () => {
    setActiveFeature(null);
  };

  // Feature configurations
  const features = {
    inventory: {
      title: 'Gestão de Estoque de Produtos',
      description: 'Gerencie o estoque de produtos, níveis de estoque e informações dos produtos',
      icon: '📦',
      component: ProductInventoryManagement
    },
    retired: {
      title: 'Gerenciar Produtos Aposentados',
      description: 'Gerencie produtos aposentados/ocultos, restaure ou gerencie permanentemente',
      icon: '🗂️',
      component: RetiredProductsManagement
    },
    clients: {
      title: 'Gestão de Clientes',
      description: 'Gerencie informações dos clientes, dados de contato e endereços',
      icon: '👥',
      component: ClientManagement
    },
    quotations: {
      title: 'Gestão de Cotações',
      description: 'Visualize e gerencie todas as cotações do sistema',
      icon: '📋',
      component: QuotationsManagement
    }
  };

  // If a specific feature is selected, show that feature
  if (activeFeature) {
    const feature = features[activeFeature];
    const FeatureComponent = feature.component;
    
    if (activeFeature === 'inventory' || activeFeature === 'retired' || activeFeature === 'clients' || activeFeature === 'quotations') {
      return <FeatureComponent onBack={handleBackToDashboard} />;
    } else {
      return <FeatureComponent 
        title={feature.title}
        description={feature.description}
        icon={feature.icon}
        onBack={handleBackToDashboard}
      />;
    }
  }

  // Main dashboard with cards
  return (
    <div className="admin-dashboard">
      <AdminHeader title="Painel Administrativo" />

      <main className="dashboard-main">
        <div className="dashboard-content">
          <h2>Painel de Controle</h2>
          
          {/* Features Overview Card */}
          <div className="features-overview-card">
            <div className="card-icon">⚙️</div>
            <div className="card-content">
              <h3>Recursos de Gerenciamento</h3>
              <p>Acesse todas as funcionalidades administrativas disponíveis</p>
              <div className="feature-count">
                <span className="count">4</span>
                <span className="label">Recursos Ativos</span>
              </div>
            </div>
          </div>

          <h3>Funcionalidades Disponíveis</h3>
          <div className="feature-cards">
            <div className="feature-card" onClick={() => handleFeatureClick('inventory')}>
              <div className="card-icon">📦</div>
              <h3>Gestão de Estoque de Produtos</h3>
              <p>Gerencie o estoque de produtos, níveis de estoque e informações dos produtos</p>
              <div className="card-status active">Disponível</div>
            </div>

            <div className="feature-card" onClick={() => handleFeatureClick('retired')}>
              <div className="card-icon">🗂️</div>
              <h3>Gerenciar Produtos Aposentados</h3>
              <p>Gerencie produtos aposentados/ocultos, restaure ou gerencie permanentemente</p>
              <div className="card-status active">Disponível</div>
            </div>

            <div className="feature-card" onClick={() => handleFeatureClick('clients')}>
              <div className="card-icon">👥</div>
              <h3>Gestão de Clientes</h3>
              <p>Gerencie informações dos clientes, dados de contato e endereços</p>
              <div className="card-status active">Disponível</div>
            </div>

            <div className="feature-card" onClick={() => handleFeatureClick('quotations')}>
              <div className="card-icon">📋</div>
              <h3>Gestão de Cotações</h3>
              <p>Visualize e gerencie todas as cotações do sistema</p>
              <div className="card-status active">Disponível</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Product Inventory Management Component
const ProductInventoryManagement = ({ onBack }) => {
  const { token } = useAuth();
  const productsHook = useProducts(token);

  return (
    <div className="admin-dashboard">
      <AdminHeader 
        title="Gestão de Estoque de Produtos" 
        onBack={onBack}
        showBackButton={true}
      />

      <main className="dashboard-main">
        <div className="breadcrumb">
          <span className="breadcrumb-item" onClick={onBack}>Painel Administrativo</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item active">Gestão de Estoque de Produtos</span>
        </div>

        <div className="dashboard-content">
          <SearchBar 
            searchTerm={productsHook.searchTerm}
            setSearchTerm={productsHook.setSearchTerm}
            hasChanges={productsHook.hasChanges}
            saveChanges={productsHook.saveChanges}
            undoChanges={productsHook.undoChanges}
            updating={productsHook.updating}
            tableLocked={productsHook.tableLocked}
            editedData={productsHook.editedData}
            error={productsHook.error}
            products={productsHook.products}
            refreshFromServer={productsHook.refreshFromServer}
            loading={productsHook.loading}
          />
          
          {productsHook.loading ? (
            <div className="loading-overlay">
              <div className="spinner"></div>
              <p>Carregando produtos...</p>
            </div>
          ) : (
            <ProductTable 
              products={productsHook.filteredProducts || []}
              updateProduct={productsHook.updateProduct}
              revertProduct={productsHook.revertProduct}
              handleSort={productsHook.handleSort}
              getSortIndicator={productsHook.getSortIndicator}
              isLowStock={productsHook.isLowStock}
              formatPrice={productsHook.formatPrice}
              updating={productsHook.updating}
              tableLocked={productsHook.tableLocked}
              clearEditingState={productsHook.clearEditingState}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
